import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { repositoryResolver, } from '@rammetec/data-access';
import { useSessionContext } from './SessionContext.js';
const RepositoryResolverContext = createContext(null);
function defaultBuildContext({ currentOrgId }) {
    return currentOrgId ? { orgId: currentOrgId } : {};
}
function toContextKey(context) {
    const entries = Object.entries(context).sort(([a], [b]) => a.localeCompare(b));
    if (entries.length === 0)
        return '__global__';
    return entries.map(([key, value]) => `${key}:${value}`).join('|');
}
export function RepositoryResolverProvider({ children, resolver = repositoryResolver, buildContext = defaultBuildContext, }) {
    const { currentOrgId } = useSessionContext();
    const [configsLoading, setConfigsLoading] = useState(true);
    const [configsError, setConfigsError] = useState(null);
    const currentContext = useMemo(() => buildContext({ currentOrgId }), [buildContext, currentOrgId]);
    useEffect(() => {
        let cancelled = false;
        setConfigsLoading(true);
        setConfigsError(null);
        resolver
            .setContext(currentContext)
            .catch((error) => {
            if (!cancelled) {
                setConfigsError(error instanceof Error ? error : new Error(String(error)));
            }
        })
            .finally(() => {
            if (!cancelled)
                setConfigsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [resolver, currentContext]);
    const refreshConfigs = useCallback(async () => {
        setConfigsLoading(true);
        setConfigsError(null);
        try {
            resolver.invalidateContext(toContextKey(currentContext));
            await resolver.setContext(currentContext);
        }
        catch (error) {
            setConfigsError(error instanceof Error ? error : new Error(String(error)));
        }
        finally {
            setConfigsLoading(false);
        }
    }, [resolver, currentContext]);
    const invalidateContext = useCallback((contextKey) => {
        resolver.invalidateContext(contextKey);
    }, [resolver]);
    const value = useMemo(() => ({
        resolver,
        isReady: !configsLoading,
        currentContext,
        currentOrgId: currentContext.orgId ?? null,
        configsLoading,
        configsError,
        refreshConfigs,
        invalidateContext,
    }), [resolver, configsLoading, currentContext, configsError, refreshConfigs, invalidateContext]);
    return (_jsx(RepositoryResolverContext.Provider, { value: value, children: children }));
}
export function useRepositoryResolver() {
    const ctx = useContext(RepositoryResolverContext);
    if (!ctx) {
        throw new Error('useRepositoryResolver must be used within a <RepositoryResolverProvider>.');
    }
    return ctx;
}
//# sourceMappingURL=RepositoryResolverContext.js.map